import { useMemo, useState } from 'react'
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, UserX } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { AvatarInfo, Button } from '@chillout/ui'
import { Card } from '../../shared/Card'
import { AdminTabs } from '../shared/AdminTabs'
import type { AdminTab } from '../shared/AdminTabs'
import { SectionErrorState } from '../shared/SectionErrorState'
import { useAdminMemberDetail } from './useAdminMemberDetail'
import { StampsTabContent } from './StampsTabContent'
import { RewardsTabContent } from './RewardsTabContent'
import { RecentActivityPanel } from './RecentActivityPanel'
import { AnonymiseConfirmModal } from './AnonymiseConfirmModal'
import { useAnonymiseMember } from './useAnonymiseMember'

interface MemberDetailViewProps {
  memberId: string
  onBack: () => void
}

type DetailTab = 'stamps' | 'rewards'

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

function formatDate(dateString: string, language: string): string {
  return new Date(dateString).toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function MemberDetailView({ memberId, onBack }: MemberDetailViewProps) {
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState<DetailTab>('stamps')
  const [showAnonymise, setShowAnonymise] = useState(false)
  const anonymiseMutation = useAnonymiseMember()

  const detailQuery = useAdminMemberDetail(memberId, i18n.language)
  const detail = detailQuery.data

  const tabs = useMemo<AdminTab<DetailTab>[]>(
    () => [
      { key: 'stamps', label: t('members.detail.tabs.stamps') },
      { key: 'rewards', label: t('members.detail.tabs.rewards') },
    ],
    [t],
  )

  if (detailQuery.isLoading) {
    return (
      <View className="flex-1 items-center justify-center py-20">
        <ActivityIndicator size="large" color={colors.accent.DEFAULT} />
      </View>
    )
  }

  if (detailQuery.isError || !detail) {
    return (
      <ScrollView className="flex-1" contentContainerClassName="grow px-6 py-8 lg:px-6 lg:py-10 xl:px-8">
        <View className="w-full max-w-[1280px] self-center">
          <Pressable
            onPress={onBack}
            accessibilityRole="button"
            accessibilityLabel={t('members.detail.backToResults')}
            className="mb-6 flex-row items-center gap-2 self-start"
          >
            <ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />
            <Text className="text-sm font-medium text-secondary">
              {t('members.detail.backToResults')}
            </Text>
          </Pressable>
          <SectionErrorState
            message={t('members.detail.loadError')}
            retryLabel={t('common.retry')}
            onRetry={() => void detailQuery.refetch()}
          />
        </View>
      </ScrollView>
    )
  }

  const fullName = getFullName(detail.firstName, detail.lastName)

  return (
    <ScrollView className="flex-1" contentContainerClassName="grow px-6 py-8 lg:px-6 lg:py-10 xl:px-8">
      <View className="w-full max-w-[1280px] self-center">
        <Pressable
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel={t('members.detail.backToResults')}
          className="mb-6 flex-row items-center gap-2 self-start"
        >
          <ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />
          <Text className="text-sm font-medium text-secondary">
            {t('members.detail.backToResults')}
          </Text>
        </Pressable>

        <View className="gap-4 lg:flex-row lg:items-start lg:justify-between">
          <AvatarInfo
            name={fullName}
            email={detail.email}
            subtitle={t('members.detail.memberSince', { date: formatDate(detail.createdAt, i18n.language) })}
            size={64}
          />
          <Button
            variant="outline"
            label={t('members.detail.anonymiseButton')}
            LeftIcon={UserX}
            fullWidth={false}
            onPress={() => setShowAnonymise(true)}
            className="self-start"
          />
        </View>

        <View className="mt-8 gap-6 lg:flex-row">
          <Card variant="dashboard" className="flex-1 p-6">
            <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            <View className="mt-6">
              {activeTab === 'stamps' ? (
                <StampsTabContent detail={detail} />
              ) : (
                <RewardsTabContent detail={detail} />
              )}
            </View>
          </Card>

          <View className="w-full lg:w-[40%] lg:shrink-0">
            <RecentActivityPanel activity={detail.recentActivity} memberEmail={detail.email} />
          </View>
        </View>
      </View>

      <AnonymiseConfirmModal
        visible={showAnonymise}
        memberName={fullName}
        memberEmail={detail.email}
        loading={anonymiseMutation.isPending}
        onConfirm={() => {
          anonymiseMutation.mutate(memberId, {
            onSuccess: () => {
              setShowAnonymise(false)
              onBack()
            },
          })
        }}
        onCancel={() => setShowAnonymise(false)}
      />
    </ScrollView>
  )
}